"use client";

import React from "react";
import { useHistory } from "react-router-dom";
import { Button } from "../../../shadcn/ui/button";
import { Plus, Calendar, Users, DollarSign, Eye, MoreHorizontal } from "lucide-react";
import logger from "../../../utils/logger";


const HostPlayServices = () => {
  const history = useHistory();

  const handleCreateService = () => {
    logger.log("Create service button clicked, navigating to:", "/host/play-services/create");
    history.push("/host/play-services/create");
  };

  // Mock data for play services
  const playServices = [
    {
      id: 1,
      title: "Sunset Yoga on the Beach",
      category: "Wellness",
      date: "2024-02-15",
      time: "6:00 PM",
      location: "Santa Monica Beach",
      capacity: 20,
      price: 45,
      bookings: 12,
      status: "Active",
      image: "/beach-yoga.jpg",
    },
    {
      id: 2,
      title: "Wine Tasting Experience",
      category: "Dining",
      date: "2024-02-20",
      time: "7:00 PM",
      location: "Napa Valley Winery",
      capacity: 15,
      price: 85,
      bookings: 8,
      status: "Active",
      image: "/wine-tasting.jpg",
    },
    {
      id: 3,
      title: "Mountain Hiking Adventure",
      category: "Adventure",
      date: "2024-02-25",
      time: "8:00 AM",
      location: "Yosemite National Park",
      capacity: 12,
      price: 65,
      bookings: 5,
      status: "Draft",
      image: "/mountain-hiking.jpg",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Play Services</h1>
              <p className="mt-1 text-sm text-gray-500">Manage your experiences and activities</p>
            </div>
            <Button onClick={handleCreateService} className="gap-2" {...({} as any)}>
              <Plus className="h-4 w-4" />
              Create New Service
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Services</p>
                <p className="text-2xl font-bold text-gray-900">{playServices.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Bookings</p>
                <p className="text-2xl font-bold text-gray-900">
                  {playServices.reduce((sum, service) => sum + service.bookings, 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                ${playServices.reduce((sum, service) => sum + service.price * service.bookings, 0)}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Eye className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Services</p>
                <p className="text-2xl font-bold text-gray-900">
                  {playServices.filter(service => service.status === "Active").length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Services List */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Your Play Services</h2>
          </div>

          <div className="divide-y divide-gray-200">
            {playServices.map(service => (
              <div key={service.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <img className="h-16 w-16 rounded-lg object-cover" src={service.image} alt={service.title} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-medium text-gray-900 truncate">{service.title}</h3>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            service.status === "Active"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {service.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">{service.category}</p>
                      <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {service.date} at {service.time}
                        </span>
                        <span className="flex items-center">
                          <Users className="h-4 w-4 mr-1" />
                          {service.bookings}/{service.capacity} booked
                        </span>
                        <span className="flex items-center">
                          <DollarSign className="h-4 w-4 mr-1" />${service.price}/person
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => history.push(`/host/play-services/edit/${service.id}`)}
                      {...({} as any)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => history.push(`/host/play-services/edit/${service.id}`)}
                      {...({} as any)}
                    >
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" {...({} as any)}>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HostPlayServices;
